import { find } from 'lodash-es';
import type { CatalogPackage, PricingCatalogResult } from 'services/api/pay';
import { narrowPriceZone, type PriceZoneCode } from './priceZone';

// 目录数据 → 旧版 PackageInfoV2 形态的适配器：
// fett 的套餐/时长选择面板（fett 风格保留）继续消费 V2 结构，
// regionPackageId / revision 透传用于新口径报价与下单。
// 适配后的时长 id：1=月付、2=年付（连续包月 id=5 由面板注入，与本表无冲突）。
// packageId 仅作选择键（目录按 accountNum 升序，序号唯一），展示名走档位名解析。

type TranslateFn = {
  (key: string, params?: any): string;
  (input: { key: string; defaultValue?: string }): string;
};

// ── 档位展示名（对齐 perseus subscription-info regionPackageDisplayName）──
// 跨境（CN）价区档位即存量老套餐：优先走老套餐词条 套餐.{versionId}（全语言已配），
// 无 versionId（账单行/支付弹窗仅有档位码 id）按档位码取同款老套餐词条；
// 非 CN 价区走 zoneKey 词条（缺词回退英文）；企业定制套餐复用已有的全语言企业版词条，
// 其余档位走 subscription.tier.*，档位码不可识别时回退名字原文。
const REGION_PACKAGE_TIER_NAMES: Record<string, { zh?: string; en: string; zoneKey: string }> = {
  TRIAL: { zh: '试用版', en: 'Trial', zoneKey: 'subscription.tier.trial' },
  PERSONAL: { zh: '个人版', en: 'Personal', zoneKey: 'subscription.tier.personal' },
  STARTER: { zh: '初创版', en: 'Starter', zoneKey: 'subscription.tier.starter' },
  BASIC: { zh: '基础版', en: 'Basic', zoneKey: 'subscription.tier.basic' },
  ADVANCED: { zh: '进阶版', en: 'Advanced', zoneKey: 'subscription.tier.advanced' },
  PREMIUM: { zh: '高阶版', en: 'Premium', zoneKey: 'subscription.tier.premium' },
  PROFESSIONAL: { zh: '专业版', en: 'Professional', zoneKey: 'subscription.tier.professional' },
  ENTERPRISE: { zh: '企业版', en: 'Enterprise', zoneKey: 'market.企业版' },
  // fett 存量外的新价格体系档位（CN 价区也无老套餐 versionId 映射）：
  // 全价区统一走 zoneKey 新项目词条（CN 中文名 成长版/扩展版/专业版 配置在 Tolgee 新项目）
  GROWTH: { en: 'Growth', zoneKey: 'subscription.tier.growth' },
  SCALE: { en: 'Scale', zoneKey: 'subscription.tier.scale' },
  PRO: { en: 'Pro', zoneKey: 'subscription.tier.pro' },
};

/** CN 档位名（中/英 comboName 实证）→ 档位码（tier 码不可用时的兜底键） */
const REGION_PACKAGE_CN_TIER_NAMES: Record<string, string> = {
  试用版: 'TRIAL',
  个人版: 'PERSONAL',
  初创版: 'STARTER',
  基础版: 'BASIC',
  进阶版: 'ADVANCED',
  专业版: 'PROFESSIONAL',
  高阶版: 'PREMIUM',
  企业版: 'ENTERPRISE',
  Trial: 'TRIAL',
  Personal: 'PERSONAL',
  Starter: 'STARTER',
  Basic: 'BASIC',
  Advanced: 'ADVANCED',
  Premium: 'PREMIUM',
  Professional: 'PROFESSIONAL',
  Enterprise: 'ENTERPRISE',
};

/** regionPackageId 档位段的价区本地化名 → 档位码（LATAM 本地化 id 实证） */
const REGION_PACKAGE_ID_TIER_ALIASES: Record<string, string> = {
  CRESCIMENTO: 'GROWTH',
  ESCALA: 'SCALE',
  AVANÇADO: 'ADVANCED',
  AVANCADO: 'ADVANCED',
};

function resolveTierCode(candidate: string | undefined): string | undefined {
  if (!candidate) return undefined;
  return candidate in REGION_PACKAGE_TIER_NAMES
    ? candidate
    : REGION_PACKAGE_ID_TIER_ALIASES[candidate];
}

/** 档位码 → 老套餐词条 id（套餐.*；跨境档位即存量老套餐，同档位多 id 词条同文取其一） */
const TIER_LEGACY_PACKAGE_IDS: Record<string, number> = {
  TRIAL: 0,
  PERSONAL: 1,
  STARTER: 2,
  ADVANCED: 3,
  PROFESSIONAL: 4,
  ENTERPRISE: 5,
  BASIC: 6,
  PREMIUM: 10,
};

/** 档位候选名 → 档位码 + 词条（tier 码 / 价区本地化 id 段 / CN 中英档位名兜底） */
function regionPackageTier(pkg: {
  tierCode?: string | null;
  regionPackageId?: string | null;
  displayName?: string | null;
}): { code: string; tier: { zh?: string; en: string; zoneKey: string } } | undefined {
  for (const code of [
    resolveTierCode(pkg.tierCode?.toUpperCase()),
    // 分隔符容忍下划线与空格（账单行 LATAM 价区 id 为「LATAM CRESCIMENTO」空格结构）
    resolveTierCode(pkg.regionPackageId?.split(/[_\s]+/)[1]?.toUpperCase()),
    REGION_PACKAGE_CN_TIER_NAMES[pkg.displayName ?? ''],
  ]) {
    if (code) {
      const tier = REGION_PACKAGE_TIER_NAMES[code];
      if (tier) return { code, tier };
    }
  }
  return undefined;
}

/**
 * 档位展示名（对齐 perseus regionPackageDisplayName）：
 * CN 价区优先老套餐词条 套餐.{versionId}（跨境档位即存量老套餐，全语言已配），无 versionId
 * 的（账单行/支付弹窗）按档位码取同款老套餐词条，新体系档位（无映射）走 zoneKey 词条；
 * 非 CN 价区统一走 zoneKey 词条（缺词回退英文）；
 * 档位码不可识别时回退名字（displayName/comboName）原文。
 */
export const regionPackageDisplayName = (
  pkg:
    | {
        tierCode?: string | null;
        regionPackageId?: string | null;
        displayName?: string | null;
        versionId?: number | null;
      }
    | undefined,
  zone: PriceZoneCode | undefined,
  t: TranslateFn,
): string => {
  if (!pkg) return '';
  const name = pkg.displayName ?? '';
  if (!name && !pkg.tierCode && !pkg.regionPackageId) return '';
  const resolved = regionPackageTier(pkg);
  if (resolved) {
    const { code, tier } = resolved;
    if (zone === 'CN') {
      // 老套餐词条全语言已配：目录 versionId 优先，缺省按档位码取同款词条
      //（查不回原 key 才算命中，缺配继续往下兜底）
      const legacyId = pkg.versionId ?? TIER_LEGACY_PACKAGE_IDS[code];
      if (legacyId != null) {
        const legacyKey = `套餐.${legacyId}`;
        const legacy = t(legacyKey);
        if (legacy !== legacyKey) return legacy;
      }
      // 新体系档位（无老套餐映射）走 zoneKey 词条，仍缺词则直出中文档位名兜底
      return tier.zh ? t(tier.zh) : t({ key: tier.zoneKey, defaultValue: tier.en });
    }
    return t({ key: tier.zoneKey, defaultValue: tier.en });
  }
  return name;
};

/** 仅凭 regionPackageId 解析档位展示名（zone 取 id 前缀；账单行/已订卡片同 perseus 口径） */
export const regionPackageIdLabel = (regionPackageId: string, t: TranslateFn): string => {
  return regionPackageDisplayName(
    { regionPackageId },
    narrowPriceZone(regionPackageId.split(/[_\s]+/)[0]),
    t,
  );
};

const MONTHLY_DURATION_ID = 1 as Pay.DurationId;
const ANNUAL_DURATION_ID = 2 as Pay.DurationId;
const SUBSCRIPTION_DURATION_ID = 5 as Pay.DurationId;

/**
 * 时长档位展示名：-1 连续包月；>= 12 归一年付档（目录 annualMonths 原值只作报价/下单传参，
 * 展示统一按 套餐时长.12「一年」口径）；其余按档位词条。
 */
export const durationLabel = (durationNum: number | undefined, t: TranslateFn): string => {
  if (durationNum === undefined) return '';
  if (durationNum === -1) return t('连续包月');
  return t(`套餐时长.${durationNum >= 12 ? 12 : durationNum}`);
};

/** 套餐展示名：目录适配时已解析为档位展示名，直接输出（缺省回退老标签） */
export const catalogPackageLabel = (pkg: Pay.PackageV2 | undefined, t: TranslateFn): string => {
  if (!pkg) return '';
  return pkg.displayName ? t(pkg.displayName) : t(`套餐.${pkg.id}` as any);
};

/**
 * 套餐 id → 展示名：新版套餐（有 regionPackageId）走档位码解析（zone 取 id 前缀，
 * 对齐 perseus 账单行 regionPackageIdLabel）；老套餐走 套餐.* 词条，
 * 查无词条时回退传入名称，避免把原始 key 渲染到界面。
 */
export const packageIdLabel = (
  id: Pay.PackageId | undefined,
  t: TranslateFn,
  fallbackName?: string,
  regionPackageId?: string | null,
): string => {
  if (regionPackageId) {
    const label = regionPackageIdLabel(regionPackageId, t);
    if (label) return label;
  }
  if (id === undefined) return fallbackName ?? '';
  const key = `套餐.${id}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return fallbackName || key;
};

export const catalogToPackageInfoV2 = (
  catalog: PricingCatalogResult | undefined,
  t: TranslateFn,
): Pay.PackageInfoV2 | undefined => {
  if (!catalog || !catalog.packages?.length) return undefined;

  // 档位展示名解析的价区口径（CN 中文、非 CN zoneKey 词条/英文）
  const zone = narrowPriceZone(catalog.priceZone);

  const packageData: Pay.PackageV2[] = catalog.packages.map((pkg, index) => {
    const prices = pkg.prices ?? [];
    // 年付报价/下单传目录 annualMonths 原值：服务端 durationNum >= 12 判年付、按 annualMonths 发
    // 服务时长，传原值保证报价时长与实际发放一致（目录缺省回退 12，对齐 perseus
    // purchase「cycle === 'annual' ? 12 : ...」口径）；展示侧经 durationLabel 归一到年付档
    const annualDurationNum = (pkg.annualMonths ?? 12) as Pay.DurationNum;
    const annualName = pkg.giftMonths
      ? t('12个月（赠{x}个月）', { x: pkg.giftMonths })
      : t('12个月');
    const displayName = regionPackageDisplayName(
      {
        tierCode: pkg.tierCode,
        regionPackageId: pkg.regionPackageId,
        displayName: pkg.displayName,
        versionId: pkg.versionId,
      },
      zone,
      t,
    );

    return {
      // 目录档位已按 accountNum 升序，序号作唯一选择键；展示名走档位名解析
      id: (index + 1) as Pay.PackageId,
      packageName: displayName,
      displayName,
      accountNum: pkg.accountNum ?? 0,
      aiServiceBuyerNum: pkg.aiServiceBuyerNum ?? 0,
      aiOrderMonthlyQuota: pkg.aiOrderMonthlyQuota ?? 0,
      regionPackageId: pkg.regionPackageId,
      durationDesc: [
        {
          id: MONTHLY_DURATION_ID,
          name: t('1个月'),
          durationNum: 1,
          price: 0,
          currencyAmount: prices.map((price) => ({
            currency: price.currency as General.Currency,
            price: Number(price.monthlyAmount ?? 0),
          })),
        },
        {
          id: ANNUAL_DURATION_ID,
          name: annualName,
          durationNum: annualDurationNum,
          giftMonths: pkg.giftMonths,
          annualActivity: pkg.annualActivity,
          price: 0,
          currencyAmount: prices.map((price) => ({
            currency: price.currency as General.Currency,
            price: Number(price.annualAmount ?? 0),
          })),
        },
      ],
    };
  });

  const recommendPkg = find(packageData, {
    regionPackageId: catalog.recommendData?.recommendRegionPackageId,
  });
  const recommendDurationNum = catalog.recommendData?.recommendDurationNum;
  // 推荐落点（对齐 perseus purchase setCycle：recommendedDuration === 1 ? monthly（默认连续
  // 订阅，等效连续包月卡）: annual；perseus 初始 cycle 即 annual）——推荐标与默认选中同源
  const recommendDurationId = (
    recommendDurationNum === 1 ? SUBSCRIPTION_DURATION_ID : ANNUAL_DURATION_ID
  ) as Pay.DurationId;

  return {
    packageData,
    recommendData: {
      recommendPackageId: recommendPkg?.id ?? packageData[0]?.id,
      recommendDurationId,
    },
    revision: catalog.revision,
    priceZone: narrowPriceZone(catalog.priceZone),
  };
};

/** 套餐在目录下的币种集合（对齐 perseus：候选以选中套餐的目录价目为界） */
export const catalogPackageCurrencies = (pkg: Pay.PackageV2 | undefined): General.Currency[] => {
  const set = new Set<General.Currency>();
  (pkg?.durationDesc ?? []).forEach((duration) => {
    duration.currencyAmount.forEach((price) => {
      if (price.currency) set.add(price.currency);
    });
  });
  return [...set];
};

/** 目录内定位当前套餐（对齐 perseus findCurrentCatalogPackage）：regionPackageId 精确匹配；
 * 旧数字 id 仅 CN 价区按 versionId 兜底（versionId 数值跨价区不可比，不作通用比较键）。 */
const findCurrentCatalogPackage = (
  packages: CatalogPackage[],
  regionPackageId: string | undefined,
  zone: PriceZoneCode | undefined,
): CatalogPackage | undefined => {
  if (!regionPackageId) return undefined;
  const current = packages.find((item) => item.regionPackageId === regionPackageId);
  if (current) return current;
  if (zone !== 'CN' || !/^\d+$/.test(regionPackageId)) return undefined;
  return packages.find((item) => item.versionId === Number(regionPackageId));
};

/**
 * 升级禁选档位（口径对齐 perseus new-pricing upgradeCandidates 的候选规则，展示形态按
 * fett 拍板保留全部档位、低版本置灰禁选）：返回「不高于当前档位」的目录套餐 ID。
 * 档位序 = 目录位次（服务端保证按 accountNum 升序）；当前套餐不在目录时回退坐席数比较，
 * 当前套餐缺失（老数据无快照）则不禁选。
 */
export const disabledUpgradeTierIds = (
  catalog: PricingCatalogResult | undefined,
  working: Pay.OrderedPackage | undefined,
): string[] => {
  if (!catalog?.packages?.length || !working) return [];
  const current = findCurrentCatalogPackage(
    catalog.packages,
    working.regionPackageId ?? String(working.id),
    narrowPriceZone(catalog.priceZone),
  );
  const workingIndex = current ? catalog.packages.indexOf(current) : -1;
  return catalog.packages
    .filter((item, index) =>
      workingIndex >= 0
        ? index <= workingIndex
        : (item.accountNum ?? 0) <= (working.accountNum ?? 0),
    )
    .map((item) => item.regionPackageId)
    .filter((id): id is string => !!id);
};
